/**
 * Color scale (colormap) configuration for the wind speed field.
 * A scale is { presetName, stops } with stops = [{ pos: 0..1, color: '#rrggbb' }].
 * Positions are normalized so a scale works for any windSpeedRange.
 */

export const COLOR_SCALE_PRESETS = {
  'CFD Rainbow': [
    { pos: 0.0, color: '#7a1fd0' },
    { pos: 0.13, color: '#3a3fd8' },
    { pos: 0.26, color: '#1e7fff' },
    { pos: 0.38, color: '#00cfe0' },
    { pos: 0.52, color: '#2bd22b' },
    { pos: 0.68, color: '#ffe61e' },
    { pos: 0.84, color: '#ff8c1a' },
    { pos: 1.0, color: '#e0341e' },
  ],
  Jet: [
    { pos: 0.0, color: '#00007f' },
    { pos: 0.125, color: '#0000ff' },
    { pos: 0.375, color: '#00ffff' },
    { pos: 0.625, color: '#ffff00' },
    { pos: 0.875, color: '#ff0000' },
    { pos: 1.0, color: '#7f0000' },
  ],
  Turbo: [
    { pos: 0.0, color: '#30123b' },
    { pos: 0.14, color: '#4662d8' },
    { pos: 0.27, color: '#2aabee' },
    { pos: 0.41, color: '#1be5b5' },
    { pos: 0.54, color: '#74fe5d' },
    { pos: 0.65, color: '#c9ef34' },
    { pos: 0.77, color: '#fbb938' },
    { pos: 0.87, color: '#f56918' },
    { pos: 0.95, color: '#c92903' },
    { pos: 1.0, color: '#7a0403' },
  ],
  Legacy: [
    { pos: 0.0, color: '#0000ff' },
    { pos: 0.15, color: '#00ffff' },
    { pos: 0.3, color: '#00ff00' },
    { pos: 0.45, color: '#ffff00' },
    { pos: 0.6, color: '#ff0000' },
    { pos: 1.0, color: '#ff0000' },
  ],
};

export const DEFAULT_PRESET_NAME = 'CFD Rainbow';
export const DEFAULT_COLOR_STOPS = COLOR_SCALE_PRESETS[DEFAULT_PRESET_NAME];
export const STORAGE_KEY = 'windSimulator.colorScale.v1';

export function hexToRgb01(hex) {
  if (typeof hex !== 'string') return null;
  const match = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!match) return null;
  const n = parseInt(match[1], 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

export function rgb01ToHex(rgb) {
  const toByte = (v) =>
    Math.round(Math.min(1, Math.max(0, v)) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toByte(rgb[0])}${toByte(rgb[1])}${toByte(rgb[2])}`;
}

export function normalizeStops(stops) {
  if (!Array.isArray(stops) || stops.length < 2) return null;
  const cleaned = [];
  for (const stop of stops) {
    if (!stop || typeof stop.pos !== 'number' || !Number.isFinite(stop.pos) || !hexToRgb01(stop.color)) {
      return null;
    }
    cleaned.push({ pos: Math.min(1, Math.max(0, stop.pos)), color: stop.color.toLowerCase() });
  }
  cleaned.sort((a, b) => a.pos - b.pos);
  return cleaned;
}

export function applyStopsToLookupTable(ctf, stops, range) {
  const normalized = normalizeStops(stops);
  if (!normalized) return false;
  const [min, max] = range;
  ctf.removeAllPoints();
  normalized.forEach(({ pos, color }) => {
    const [r, g, b] = hexToRgb01(color);
    ctf.addRGBPoint(min + pos * (max - min), r, g, b);
  });
  return true;
}

export function stopsToCssGradient(stops) {
  const normalized = normalizeStops(stops) || DEFAULT_COLOR_STOPS;
  const parts = normalized.map(({ pos, color }) => `${color} ${Math.round(pos * 100)}%`);
  return `linear-gradient(to right, ${parts.join(', ')})`;
}

export function loadColorScale() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const stops = normalizeStops(parsed.stops);
      if (stops) {
        return {
          presetName: typeof parsed.presetName === 'string' ? parsed.presetName : 'Custom',
          stops,
        };
      }
    }
  } catch (error) {
    // corrupt storage or storage unavailable: fall back to default
  }
  return { presetName: DEFAULT_PRESET_NAME, stops: DEFAULT_COLOR_STOPS };
}

export function saveColorScale(scale) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(scale));
  } catch (error) {
    // storage unavailable (private mode/quota): persistence is best-effort
  }
}
